import type { Square } from './types';

const FILES = 'abcdefgh';
const KNIGHT_DELTAS: ReadonlyArray<readonly [number, number]> = [
  [1, 2],
  [1, -2],
  [-1, 2],
  [-1, -2],
  [2, 1],
  [2, -1],
  [-2, 1],
  [-2, -1],
];

function fileIndex(sq: Square): number {
  return FILES.indexOf(sq[0]!);
}

function rankIndex(sq: Square): number {
  return Number(sq[1]) - 1;
}

function toSquare(file: number, rank: number): Square | null {
  if (file < 0 || file > 7 || rank < 0 || rank > 7) return null;
  return `${FILES[file]}${rank + 1}` as Square;
}

export function knightNeighbors(start: Square): Square[] {
  const f = fileIndex(start);
  const r = rankIndex(start);
  const out: Square[] = [];
  for (const [df, dr] of KNIGHT_DELTAS) {
    const sq = toSquare(f + df, r + dr);
    if (sq) out.push(sq);
  }
  return out;
}

/** Shortest knight-move distance from `start` to every square. */
export function knightDistances(start: Square): Map<Square, number> {
  const dist = new Map<Square, number>([[start, 0]]);
  const q: Square[] = [start];
  for (let i = 0; i < q.length; i++) {
    const cur = q[i]!;
    const d = dist.get(cur)!;
    for (const n of knightNeighbors(cur)) {
      if (dist.has(n)) continue;
      dist.set(n, d + 1);
      q.push(n);
    }
  }
  return dist;
}

/**
 * Squares a knight can occupy after exactly `moves` hops, allowing
 * going backwards. Same as shortest distance `d` with `d <= moves`
 * and `moves - d` even.
 */
export function squaresAtKnightMoves(start: Square, moves: number): Square[] {
  if (moves <= 0) return [];
  const out: Square[] = [];
  for (const [sq, d] of knightDistances(start)) {
    if (d <= moves && (moves - d) % 2 === 0) out.push(sq);
  }
  return out.sort();
}

export function allSquares(): Square[] {
  const out: Square[] = [];
  for (let r = 1; r <= 8; r++) {
    for (const f of FILES) {
      out.push(`${f}${r}` as Square);
    }
  }
  return out;
}

export function randomSquare(rng: () => number = Math.random): Square {
  const squares = allSquares();
  const index = Math.min(squares.length - 1, Math.floor(rng() * squares.length));
  return squares[index]!;
}
